// src/pages/visitor/DiscoverMarketplacePage.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ShoppingBag,
  Search,
  ArrowLeft,
  Package,
  Heart,
  MessageCircle,
  DollarSign,
  Star,
  ChevronLeft,
  ChevronRight,
  X,
  Mail,
  User,
} from "lucide-react";
import { messagesApi } from "../../lib/mudoNetwork";
import { formatPrice } from "../../utils/formatters";
import { getSoldState } from "../../utils/productSold";
import ComposeMessageModal from "../../components/Messaging/ComposeMessageModal";
import InboxModal from "../../components/Messages/InboxModal";
import ProfileSetupModal from "../../components/Auth/ProfileSetupModal";
import VisitorNavBar from "../../components/Common/VisitorNavBar";
import OfferModal from "../../components/Shop/OfferModal";
import ShopPayNowModal from "../../components/ShopPayNowModal.jsx";
import ImageCarousel from "../../components/Shop/ImageCarousel";
import { useAuth } from "../../contexts/AuthContext";
import { useNotifications } from "../../contexts/NotificationsContext";
import { useUnreadCount } from "../../contexts/UnreadCountContext";
import { createCheckoutSession } from "../../api/checkoutClient";
import { apiFetch } from "../../lib/apiFetch";
import { supabase } from "../../lib/supabase";
import ListingDetailModal from "../../components/Marketplace/ListingDetailModal";
import { getPrimaryProductImage } from "../../lib/productImageUtils";

// Ultra-minimal to avoid DB timeouts until index is applied
const PAGE_SIZE = 6;
const FIRST_PAGE_CACHE_TTL_MS = 5 * 60 * 1000;
const marketplaceFirstPageCache = {
  at: 0,
  items: null,
  nextCursor: null,
};

// Monotonic request counter for marketplace fetches (dev tracing)
let marketplaceReqSeq = 0;

// Cache resolved storage URLs per product to avoid recomputation
const thumbnailUrlCache = new Map();

/**
 * DiscoverMarketplacePage - Browse ceramic products from studios
 *
 * Features:
 * - Grid of marketplace products
 * - Filters (search, price range, studio, category, location)
 * - Message and Offer buttons on each product
 * - Favorites
 */
function resolveOfferListingId(offer) {
  if (!offer) return null;
  return (
    offer.listing_product_id ||
    offer?.listing?.product_id ||
    offer.product_id ||
    offer.listing_id ||
    null
  );
}

function MarketplaceSkeletonCard() {
  return (
    <div className="bg-[#2a2b2e] border border-white/8 rounded-xl overflow-hidden flex flex-col">
      {/* Image area with shimmer */}
      <div
        className="w-full bg-gradient-to-r from-white/5 via-white/10 to-white/5 animate-pulse"
        style={{ aspectRatio: "4 / 3" }}
      />
      {/* Content area */}
      <div className="p-4 space-y-3 flex flex-col gap-2 flex-grow">
        {/* Title skeleton */}
        <div className="h-4 w-5/6 bg-white/10 rounded animate-pulse" />
        {/* Studio skeleton */}
        <div className="h-3 w-1/3 bg-white/10 rounded animate-pulse" />
        {/* Description skeleton (2 lines) */}
        <div className="space-y-1">
          <div className="h-3 w-full bg-white/10 rounded animate-pulse" />
          <div className="h-3 w-3/4 bg-white/10 rounded animate-pulse" />
        </div>
        {/* Price skeleton */}
        <div className="h-6 w-24 bg-white/10 rounded animate-pulse mt-auto" />
        {/* Buttons skeleton */}
        <div className="flex gap-2 pt-2">
          <div className="flex-1 h-9 bg-white/10 rounded animate-pulse" />
          <div className="flex-1 h-9 bg-white/10 rounded animate-pulse" />
        </div>
      </div>
    </div>
  );
}

function MarketplaceSkeletonGrid({ count = 12 }) {
  const items = Array.from({ length: count });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {items.map((_, idx) => (
        <MarketplaceSkeletonCard key={idx} />
      ))}
    </div>
  );
}

export default function DiscoverMarketplacePage() {
  const navigate = useNavigate();
  const { user, session, profile } = useAuth() || {};
  const { notify } = useNotifications() || {};
    // Defensive notify wrapper: never block marketplace rendering on notification failures
    const safeNotify = (payload) => {
      try {
        notify?.(payload);
      } catch (e) {
        console.warn("[DiscoverMarketplace] notify failed; continuing", { message: e?.message });
      }
    };
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkOfferId = searchParams.get("offerId");
  const deepLinkThreadId = searchParams.get("threadId");

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMoreSentinelRef = useRef(null);
  const inFlightRef = useRef(false);
  const didFetchRef = useRef(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [selectedStudio, setSelectedStudio] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedLocation, setSelectedLocation] = useState("all");

  // Listing detail modal state (2-step fetch pattern)
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedListingId, setSelectedListingId] = useState(null);
  const [listingDetail, setListingDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const [favorites, setFavorites] = useState(() => new Set());

  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [modalImages, setModalImages] = useState([]);
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const [modalProductTitle, setModalProductTitle] = useState("");

  const [composeModalOpen, setComposeModalOpen] = useState(false);
  const [messageRecipient, setMessageRecipient] = useState(null);
  const [messageProduct, setMessageProduct] = useState(null);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [initialThreadId, setInitialThreadId] = useState(null);
  const deepLinkThreadHandledRef = useRef(false);
  const { unreadCount } = useUnreadCount(); // Get unread count from Realtime subscription context

  const [profileModalOpen, setProfileModalOpen] = useState(false);

  const [offerProduct, setOfferProduct] = useState(null);
  const [selectedListing, setSelectedListing] = useState(null);
  const [offerAmount, setOfferAmount] = useState("");
  const [offerMessage, setOfferMessage] = useState("");
  const [offerModalOpen, setOfferModalOpen] = useState(false);
  const [offerError, setOfferError] = useState("");
  const [offerLoading, setOfferLoading] = useState(false);
  const [offerSuccess, setOfferSuccess] = useState(false);
  const [buyerOffers, setBuyerOffers] = useState([]);
  const [buyerOffersLoaded, setBuyerOffersLoaded] = useState(false);
  const [buyerOffersError, setBuyerOffersError] = useState(null);
  const buyerOffersAuthBlockedRef = useRef(false);
  const buyerOffersAuthNotifiedRef = useRef(false);
  const deepLinkOfferHandledRef = useRef(false);

  const [payModal, setPayModal] = useState({ open: false, clientSecret: null, offerId: null, returnTo: null });
  const [payModalLoading, setPayModalLoading] = useState(false);

  const loadBuyerOffers = async () => {
    if (!user?.id) {
      setBuyerOffers([]);
      setBuyerOffersLoaded(true);
      setBuyerOffersError(null);
      buyerOffersAuthBlockedRef.current = false;
      buyerOffersAuthNotifiedRef.current = false;
      return;
    }
    try {
      setBuyerOffersError(null);
      const res = await apiFetch("/api/shop/buyer-offers");
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.ok) {
        const status = res.status;
        const code = json?.code || String(status);
        const authError = status === 401 || status === 403 || code === "AUTH_REQUIRED";
        const message =
          json?.error ||
          (authError ? "Sign in required to load your offers." : "Failed to load offers.");
        setBuyerOffersError({ message, code });
        if (authError) {
          buyerOffersAuthBlockedRef.current = true;
          if (!buyerOffersAuthNotifiedRef.current) {
            buyerOffersAuthNotifiedRef.current = true;
            notify?.({
              title: "Offers",
              body: message,
              kind: "warning",
            });
          }
        }
        console.error("[Marketplace] buyer offers fetch error", { status, code, json });
        return;
      }
      setBuyerOffers(json.offers || []);
      setBuyerOffersError(null);
      buyerOffersAuthBlockedRef.current = false;
      buyerOffersAuthNotifiedRef.current = false;
    } catch (err) {
      setBuyerOffersError({
        message: err?.message || "Failed to load offers.",
        code: "NETWORK_ERROR",
      });
      console.error("[Marketplace] buyer offers fetch error", err);
    } finally {
      setBuyerOffersLoaded(true);
    }
  };

    

    useEffect(() => {
      if (didFetchRef.current) return; // Guard against StrictMode double effects
      didFetchRef.current = true;
      async function fetchFirstPage() {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      // Always fetch fresh data - disable cache to prevent showing stale/ghost listings
      const cacheFresh = false; // ← Cache disabled to fix ghost listings issue
      const cacheExpiryMs = 0; // ← Force immediate cache expiry

      if (cacheFresh && marketplaceFirstPageCache.at && (Date.now() - marketplaceFirstPageCache.at < cacheExpiryMs)) {
        console.log("[DiscoverMarketplace] Using cached first page:", {
          itemsCount: marketplaceFirstPageCache.items?.length || 0,
          cacheAgeMs: Date.now() - marketplaceFirstPageCache.at,
        });
        setProducts(marketplaceFirstPageCache.items || []);
        setNextCursor(marketplaceFirstPageCache.nextCursor || null);
        setHasMore(!!marketplaceFirstPageCache.nextCursor);
        setLoading(false);
        setError(null);
        return;
      }

      // Keep previous cache until a successful fetch completes (prevents clearing on errors)

      setLoading(true);
      setError(null);

      try {
        const requestId = `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        const seq = ++marketplaceReqSeq;
        const url = new URL("/api/public/marketplace-products", window.location.origin);
        url.searchParams.set("limit", String(PAGE_SIZE));

        console.info("[DiscoverMarketplace] Fetching first page:", {
          requestId,
          seq,
          url: url.pathname + url.search,
          limit: PAGE_SIZE,
        });

        const res = await apiFetch(url.pathname + url.search, { headers: { "x-request-id": requestId } });
        const json = await res.json().catch(() => ({}));

        console.info("[DiscoverMarketplace] Response received:", {
          requestId,
          seq,
          status: res.status,
          ok: json?.ok,
          itemsCount: Array.isArray(json?.items) ? json.items.length : 0,
          errorCode: json?.error?.code || json?.code || "none",
        });

        // Handle errors from API
        if (!res.ok || !json?.ok) {
          const errorMsg = json?.error?.message || json?.message || "Failed to load products";
          const errorCode = json?.error?.code || json?.code || "UNKNOWN";
          
          console.error("[DiscoverMarketplace] API returned error:", {
            requestId,
            seq,
            status: res.status,
            code: errorCode,
            message: errorMsg,
          });

          setError(errorMsg);
          setLoading(false);
          return;
        }

        // Debug: Log first 3 items to verify shape
        if (Array.isArray(json?.items) && json.items.length > 0) {
          console.info("[DiscoverMarketplace] Sample items returned:", {
            requestId,
            seq,
            count: json.items.length,
            firstItem: {
              id: json.items[0].id,
              title: json.items[0].title,
              price_cents: json.items[0].price_cents,
              studio_id: json.items[0].studio_id,
              has_main_image: !!json.items[0].main_image_url,
              has_gallery: Array.isArray(json.items[0].image_gallery) && json.items[0].image_gallery.length > 0,
            },
          });
        }
        const items = Array.isArray(json.items) ? json.items : null;
        if (!Array.isArray(items)) {
          console.warn("[DiscoverMarketplace] Skipping state update; items not an array", {
            requestId,
            seq,
            receivedType: typeof json?.items,
          });
          setLoading(false);
          return;
        }
        const nc = json?.nextCursor || null;

        console.info("[DiscoverMarketplace] First page loaded successfully:", {
          requestId,
          seq,
          itemsCount: items.length,
          hasMore: !!nc,
        });

        console.info("[DiscoverMarketplace] State write: setProducts(items)", {
          requestId,
          seq,
          newLength: items.length,
        });
        setProducts(items);
        setNextCursor(nc);
        setHasMore(!!nc);

        // Update cache only after successful fetch
        marketplaceFirstPageCache.at = Date.now();
        marketplaceFirstPageCache.items = items;
        marketplaceFirstPageCache.nextCursor = nc;
      } catch (err) {
        console.error("Error fetching marketplace products:", err);
        setError(err?.message || "Failed to load products. Please try again.");
      } finally {
        inFlightRef.current = false;
        setLoading(false);
      }
      }

      fetchFirstPage();
      // unreadCount is now managed by UnreadCountContext with Realtime subscription
      // No polling needed here
  }, [notify]);

  const loadMore = async () => {
      if (!hasMore || loadingMore || inFlightRef.current) return;
      if (!nextCursor?.created_at || !nextCursor?.id) {
      setHasMore(false);
      return;
      }

      inFlightRef.current = true;
      setLoadingMore(true);

      try {
        const requestId = `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        const seq = ++marketplaceReqSeq;
        const url = new URL("/api/public/marketplace-products", window.location.origin);
        url.searchParams.set("limit", String(PAGE_SIZE));
        url.searchParams.set("cursorCreatedAt", String(nextCursor.created_at));
        url.searchParams.set("cursorId", String(nextCursor.id));

        const res = await apiFetch(url.pathname + url.search, { headers: { "x-request-id": requestId } });
        const json = await res.json().catch(() => ({}));
        
        console.info("[DiscoverMarketplace] Load more response:", {
          requestId,
          seq,
          status: res.status,
          ok: json?.ok,
          itemsCount: Array.isArray(json?.items) ? json.items.length : 0,
          errorCode: json?.error?.code || "none",
        });
        
        if (!res.ok || !json?.ok) {
          const errorMsg = json?.error?.message || "Failed to load more products.";
          console.error("[DiscoverMarketplace] Load more error:", {
            status: res.status,
            code: json?.error?.code,
            message: errorMsg,
          });
          setError(errorMsg);
          setHasMore(false);
          return;
        }

        const items = Array.isArray(json.items) ? json.items : null;
        if (!Array.isArray(items)) {
          console.warn("[DiscoverMarketplace] Skipping append; items not an array", {
            requestId,
            seq,
            receivedType: typeof json?.items,
          });
          return;
        }
        const nc = json?.nextCursor || null;

        console.info("[DiscoverMarketplace] Load more succeeded:", {
          requestId,
          seq,
          itemsCount: items.length,
          hasMore: !!nc,
        });

        console.info("[DiscoverMarketplace] State write: append products", {
          requestId,
          seq,
          appendCount: items.length,
        });
        setProducts((prevProducts) => [...prevProducts, ...items]);
        setNextCursor(nc);
        setHasMore(!!nc);
      } catch (err) {
        console.error("Error loading more products:", err);
        setError(err?.message || "Failed to load more products.");
      } finally {
        inFlightRef.current = false;
        setLoadingMore(false);
      }
  };

  useEffect(() => {
        if (!deepLinkOfferId || deepLinkOfferHandledRef.current || !buyerOffersLoaded) return;
        const offer = (buyerOffers || []).find(
        (o) => String(o.id) === String(deepLinkOfferId)
        );
        const listingKey = resolveOfferListingId(offer);
        if (listingKey) {
        const el = document.getElementById(`listing-${listingKey}`);
        if (el?.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        } else {
        notify?.({
        title: "Offer not found",
        body: "We couldn’t find that offer in your marketplace view.",
        kind: "warning",
        });
        }
        deepLinkOfferHandledRef.current = true;
        try {
        const next = new URLSearchParams(searchParams);
        next.delete("offerId");
        setSearchParams(next, { replace: true });
        } catch {}
      }, [deepLinkOfferId, buyerOffersLoaded, buyerOffers, notify, searchParams, setSearchParams]);

  const studios = useMemo(() => {
    const studioMap = new Map();
    products.forEach((p) => {
      if (p.studio?.id && p.studio?.name) {
        studioMap.set(p.studio.id, p.studio);
      }
    });
    return Array.from(studioMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    try {
      const studioMap = new Map();
      products.forEach((p) => {
        if (p?.studio?.id && p?.studio?.name) {
          studioMap.set(p.studio.id, p.studio);
        }
      });
      return Array.from(studioMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    } catch (e) {
      console.warn("[DiscoverMarketplace] studios derivation failed; falling back", { message: e?.message });
      return [];
    }
  }, [products]);

  const categories = useMemo(() => {
    const catSet = new Set();
    products.forEach((p) => {
      if (p.category?.trim()) catSet.add(p.category.trim());
    });
    return Array.from(catSet).sort();
    try {
      const catSet = new Set();
      products.forEach((p) => {
        const c = p?.category;
        if (typeof c === "string" && c.trim()) catSet.add(c.trim());
      });
      return Array.from(catSet).sort();
    } catch (e) {
      console.warn("[DiscoverMarketplace] categories derivation failed; falling back", { message: e?.message });
      return [];
    }
  }, [products]);

  const locations = useMemo(() => {
    const locSet = new Set();
    products.forEach((p) => {
      if (p.studio?.city?.trim()) locSet.add(p.studio.city.trim());
    });
    return Array.from(locSet).sort();
    try {
      const locSet = new Set();
      products.forEach((p) => {
        const city = p?.studio?.city;
        if (typeof city === "string" && city.trim()) locSet.add(city.trim());
      });
      return Array.from(locSet).sort();
    } catch (e) {
      console.warn("[DiscoverMarketplace] locations derivation failed; falling back", { message: e?.message });
      return [];
    }
  }, [products]);

  const buyerOffersByListing = useMemo(() => {
    const map = {};
    (buyerOffers || []).forEach((o) => {
      const listingKey = resolveOfferListingId(o);
      if (!listingKey) return;
      if (!map[listingKey]) map[listingKey] = [];
      map[listingKey].push(o);
    });
    return map;
  }, [buyerOffers]);

  const filteredProducts = useMemo(() => {
    // Keep existing UX: filters apply instantly.
    // With pagination, this filters the currently loaded slice.
    let filtered = [...products];
    
    console.log("[DiscoverMarketplace] Filtering products:", {
      totalProducts: products.length,
      filters: {
        searchTerm: searchTerm || "none",
        minPrice: minPrice || "none",
        maxPrice: maxPrice || "none",
        selectedStudio: selectedStudio || "all",
        selectedCategory: selectedCategory || "all",
        selectedLocation: selectedLocation || "all",
      }
    });

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      const beforeCount = filtered.length;
      filtered = filtered.filter((product) => {
        const titleMatch = product.title?.toLowerCase().includes(term);
        const descMatch = product.description?.toLowerCase().includes(term);
        const studioMatch = product.studio?.name?.toLowerCase().includes(term);
        return titleMatch || descMatch || studioMatch;
      });
      console.log(`[Filter] searchTerm: ${beforeCount} → ${filtered.length}`);
    }

    if (minPrice !== "") {
      const min = parseFloat(minPrice) * 100;
      const beforeCount = filtered.length;
      filtered = filtered.filter((p) => p.price_cents >= min);
      console.log(`[Filter] minPrice: ${beforeCount} → ${filtered.length}`);
    }
    if (maxPrice !== "") {
      const max = parseFloat(maxPrice) * 100;
      const beforeCount = filtered.length;
      filtered = filtered.filter((p) => p.price_cents <= max);
      console.log(`[Filter] maxPrice: ${beforeCount} → ${filtered.length}`);
    }

    if (selectedStudio !== "all") {
      const beforeCount = filtered.length;
      filtered = filtered.filter((p) => p.studio?.id === selectedStudio);
      console.log(`[Filter] selectedStudio (${selectedStudio}): ${beforeCount} → ${filtered.length}`);
    }

    if (selectedCategory !== "all") {
      const beforeCount = filtered.length;
      filtered = filtered.filter((p) => p.category?.trim() === selectedCategory);
      console.log(`[Filter] selectedCategory (${selectedCategory}): ${beforeCount} → ${filtered.length}`);
    }

    if (selectedLocation !== "all") {
      const beforeCount = filtered.length;
      filtered = filtered.filter((p) => p.studio?.city?.trim() === selectedLocation);
      console.log(`[Filter] selectedLocation (${selectedLocation}): ${beforeCount} → ${filtered.length}`);
    }

    console.log("[DiscoverMarketplace] Final filtered count:", filtered.length);
    return filtered;
    try {
      let filtered = Array.isArray(products) ? [...products] : [];
      console.log("[DiscoverMarketplace] Filtering products:", {
        totalProducts: filtered.length,
        filters: {
          searchTerm: searchTerm || "none",
          minPrice: minPrice || "none",
          maxPrice: maxPrice || "none",
          selectedStudio: selectedStudio || "all",
          selectedCategory: selectedCategory || "all",
          selectedLocation: selectedLocation || "all",
        }
      });
      if (typeof searchTerm === "string" && searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        const beforeCount = filtered.length;
        filtered = filtered.filter((product) => {
          const titleMatch = product?.title?.toLowerCase().includes(term);
          const descMatch = product?.description?.toLowerCase().includes(term);
          const studioMatch = product?.studio?.name?.toLowerCase().includes(term);
          return titleMatch || descMatch || studioMatch;
        });
        console.log(`[Filter] searchTerm: ${beforeCount} → ${filtered.length}`);
      }
      if (minPrice !== "") {
        const min = parseFloat(minPrice) * 100;
        const beforeCount = filtered.length;
        filtered = filtered.filter((p) => (p?.price_cents ?? 0) >= min);
        console.log(`[Filter] minPrice: ${beforeCount} → ${filtered.length}`);
      }
      if (maxPrice !== "") {
        const max = parseFloat(maxPrice) * 100;
        const beforeCount = filtered.length;
        filtered = filtered.filter((p) => (p?.price_cents ?? 0) <= max);
        console.log(`[Filter] maxPrice: ${beforeCount} → ${filtered.length}`);
      }
      if (selectedStudio !== "all") {
        const beforeCount = filtered.length;
        filtered = filtered.filter((p) => p?.studio?.id === selectedStudio);
        console.log(`[Filter] selectedStudio (${selectedStudio}): ${beforeCount} → ${filtered.length}`);
      }
      if (selectedCategory !== "all") {
        const beforeCount = filtered.length;
        filtered = filtered.filter((p) => (p?.category || "").trim() === selectedCategory);
        console.log(`[Filter] selectedCategory (${selectedCategory}): ${beforeCount} → ${filtered.length}`);
      }
      if (selectedLocation !== "all") {
        const beforeCount = filtered.length;
        filtered = filtered.filter((p) => (p?.studio?.city || "").trim() === selectedLocation);
        console.log(`[Filter] selectedLocation (${selectedLocation}): ${beforeCount} → ${filtered.length}`);
      }
      console.log("[DiscoverMarketplace] Final filtered count:", filtered.length);
      return filtered;
    } catch (e) {
      console.warn("[DiscoverMarketplace] filtering failed; showing unfiltered items", { message: e?.message });
      return Array.isArray(products) ? products : [];
    }
  }, [products, searchTerm, minPrice, maxPrice, selectedStudio, selectedCategory, selectedLocation]);

  const toggleFavorite = (productId) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const handleMessage = (product) => {
    if (!user) {
      navigate("/auth");
      return;
    }
    setMessageProduct(product);
    setMessageRecipient(product.studio?.owner_user_id || null);
    setComposeModalOpen(true);
  };

  const handleOffer = (product) => {
    if (!user) {
      navigate("/auth");
      return;
    }
    setOfferProduct(product);
    setSelectedListing(product);
    setOfferAmount("");
    setOfferMessage("");
    setOfferModalOpen(true);
    setOfferError("");
    setOfferSuccess(false);
  };

  const handleCloseOfferModal = () => {
    setOfferModalOpen(false);
    setOfferProduct(null);
    setSelectedListing(null);
    setOfferAmount("");
    setOfferMessage("");
    setOfferError("");
    setOfferLoading(false);
  };

  const openImageModal = (product) => {
    const images =
      (Array.isArray(product.image_gallery) && product.image_gallery.length > 0
        ? product.image_gallery
        : product.main_image_url
        ? [product.main_image_url]
        : []) || [];

    if (images.length > 0) {
      setModalImages(images);
      setModalImageIndex(0);
      setModalProductTitle(product.title || "Product");
      setImageModalOpen(true);
    }
  };

  const nextImage = () => {
    setModalImageIndex((prev) => (prev + 1) % modalImages.length);
  };

  const prevImage = () => {
    setModalImageIndex((prev) => (prev - 1 + modalImages.length) % modalImages.length);
  };

  const handleViewProduct = async (product) => {
    // STEP 2: Fetch full listing details on demand (2-step pattern)
    // Query Supabase directly for RLS safety, avoid API routes
    const productId = product.id;
    setSelectedListingId(productId);
    setDetailModalOpen(true);
    setDetailLoading(true);
    setDetailError(null);

    try {
      // Fetch full product details from Supabase
      // Note: DO NOT select listing_id (doesn't exist in products table)
      const { data: productDetail, error: productErr } = await supabase
        .from("products")
        .select(`
          id,
          title,
          description,
          price_cents,
          currency,
          studio_id,
          image_gallery,
          main_image_url,
          stock_qty,
          category,
          condition,
          location,
          is_published,
          is_active,
          created_at,
          updated_at
        `)
        .eq("id", productId)
        .eq("is_published", true)
        .or("is_active.eq.true,is_active.is.null")
        .single();

      if (productErr || !productDetail) {
        throw new Error(productErr?.message || "Product not found");
      }

      // Normalize images: handle image_gallery as JSON string if needed
      let normalizedDetail = { ...productDetail };
      if (normalizedDetail.image_gallery) {
        try {
          // If image_gallery is a string, parse it
          if (typeof normalizedDetail.image_gallery === "string") {
            normalizedDetail.image_gallery = JSON.parse(
              normalizedDetail.image_gallery
            );
          }
        } catch (parseErr) {
          if (process.env.NODE_ENV === "development") {
            console.warn(
              "[DiscoverMarketplace] Failed to parse image_gallery:",
              parseErr
            );
          }
          normalizedDetail.image_gallery = [];
        }
      }

      // Fetch studio info for display
      let studio = null;
      if (normalizedDetail.studio_id) {
        const { data: studioData, error: studioErr } = await supabase
          .from("studios")
          .select("id,name,slug,city,country,owner_user_id")
          .eq("id", normalizedDetail.studio_id)
          .single();

        if (!studioErr && studioData) {
          studio = studioData;
        }
      }

      const detailData = { ...normalizedDetail, studio };
      setListingDetail(detailData);
    } catch (err) {
      console.error("[DiscoverMarketplace] Detail fetch error:", err);
      setDetailError(err?.message || "Failed to load listing details");
      if (notify) {
        notify({
          title: "Error loading listing",
          body: err?.message || "Please try again",
          kind: "error",
        });
      }
    } finally {
      setDetailLoading(false);
    }
  };

  const handleBuyerPayNow = async (offer) => {
    if (!offer || !user?.id) {
      if (notify) {
        notify({
          title: "Authentication required",
          body: "Please log in to proceed with payment.",
          kind: "error",
        });
      }
      return;
    }

    // Validate buyer_id matches current user (if set)
    if (offer.buyer_id && offer.buyer_id !== user.id) {
      if (notify) {
        notify({
          title: "Not your offer",
          body: "You can only pay for your own offers.",
          kind: "error",
        });
      }
      return;
    }

    setPayModalLoading(true);

    try {
      // Step 1: Ensure order exists (if offer.order_id is null, create it)
      if (!offer.order_id) {
        const ensureRes = await apiFetch(`/api/shop/offers/${offer.id}/ensure-order`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ offerId: offer.id }),
        });
        const ensureJson = await ensureRes.json().catch(() => ({}));
        if (!ensureRes.ok || !ensureJson?.ok || !ensureJson?.orderId) {
          const code = ensureJson?.code || "ENSURE_ORDER_FAILED";
          const errMsg = ensureJson?.error || "Failed to create order for this offer.";
          if (notify) {
            notify({ title: "Payment", body: `${errMsg} (${code})`, kind: "error" });
          }
          throw new Error(errMsg);
        }
        // Update local state with the new order_id
        const newOrderId = ensureJson.orderId;
        setBuyerOffers((prev) =>
          prev.map((o) =>
            o.id === offer.id ? { ...o, order_id: newOrderId } : o
          )
        );
        offer.order_id = newOrderId;
      }

      // Step 2: Get clientSecret for PaymentIntent
      const checkoutRes = await apiFetch("/api/shop/order-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orderId: offer.order_id,
          offerId: offer.id,
        }),
      });
      const checkoutJson = await checkoutRes.json().catch(() => ({}));
      if (!checkoutRes.ok || !checkoutJson?.ok || !checkoutJson?.clientSecret) {
        throw new Error(checkoutJson?.error || "Failed to initialize payment.");
      }

      // Step 3: Open Stripe Elements modal
      setPayModal({
        open: true,
        clientSecret: checkoutJson.clientSecret,
        offerId: offer.id,
        returnTo: "/discover/marketplace",
      });
    } catch (err) {
      console.error("[Marketplace] Pay now error", err);
      const msg = err?.message || "Could not start payment for this offer.";
      if (notify) {
        notify({
          title: "Payment Error",
          body: msg,
          kind: "error",
        });
      }
    } finally {
      setPayModalLoading(false);
    }
  };

  // Buyer-side delivery actions
  const handleBuyerConfirmReceived = async (offer) => {
    if (!offer) return;
    try {
      const res = await apiFetch("/api/shop/offers-status", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ offerId: offer.id, status: "completed" }),
      });
      if (!res.ok) throw new Error("Failed to confirm receipt");
      setBuyerOffers((prev) =>
        prev.map((o) => (o.id === offer.id ? { ...o, status: "completed" } : o))
      );

      // Trigger (idempotent) payout flag after completion
      try {
        const payoutRes = await apiFetch("/api/shop/offers-payout", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ offerId: offer.id }),
        });
        if (!payoutRes.ok) {
          const errorBody = await payoutRes.json().catch(() => ({}));
          console.warn("[Marketplace] offers-payout warning", {
            status: payoutRes.status,
            error: errorBody?.error || payoutRes.statusText,
          });
        }
      } catch (payoutErr) {
        console.warn("[Marketplace] offers-payout error", payoutErr);
      } finally {
        if (notify) {
          notify({
            title: "Payment",
            body: "Marked as received.",
            kind: "info",
          });
        }
      }

      await loadBuyerOffers();
    } catch (err) {
      console.error("[Marketplace] confirm received error", err);
      if (notify) {
        notify({
          title: "Payment",
          body: "Marked as received.",
          kind: "info",
        });
      }
    }
  };

  const handleBuyerReportIssue = async (offer) => {
    if (!offer) return;
    try {
      const res = await apiFetch("/api/shop/offers-status", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ offerId: offer.id, status: "disputed" }),
      });
      if (!res.ok) throw new Error("Failed to report issue");
      setBuyerOffers((prev) =>
        prev.map((o) => (o.id === offer.id ? { ...o, status: "disputed" } : o))
      );
      await loadBuyerOffers();
    } catch (err) {
      console.error("[Marketplace] report issue error", err);
      alert(err?.message || "Could not report an issue. Please try again.");
    }
  };

  const handleSubmitOffer = async (amountOverride) => {
    if (!user) {
      setOfferError("You must be logged in to make an offer.");
      return;
    }

    if (!selectedListing) {
      setOfferError("No listing selected.");
      return;
    }

    const effectiveAmount = amountOverride ?? offerAmount;

    setOfferError(null);
    setOfferLoading(true);

    try {
      // Use listing_id (shop_listings.id) for offers, not product.id\n      const listingId = selectedListing?.listing_id || selectedListing?.uuid || selectedListing?.id;
      if (!listingId) {
        setOfferError("Listing is missing an id.");
        return;
      }

      const numericAmount = Number(effectiveAmount);
      const amountCents = Math.round(numericAmount * 100);
      if (!Number.isFinite(amountCents) || amountCents <= 0) {
        setOfferError("Enter a valid offer amount.");
        return;
      }

        const payload = { listingId, amountCents };
        if (selectedListing?.id) {
          payload.productId = selectedListing.id;
        }
      if (offerMessage?.trim()) payload.message = offerMessage.trim();
      if (selectedListing?.qty != null) payload.qty = selectedListing.qty;
      else if (selectedListing?.quantity != null) payload.qty = selectedListing.quantity;

      console.log("[Offer] payload", payload);

      const res = await apiFetch("/api/shop/listings-offer", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!data?.ok) {
        const code = data?.code || "OFFER_FAILED";
        const msg = data?.error || "Could not submit your offer, please try again.";
        const err = new Error(msg);
        err.data = data;
        throw err;
      }

      // SUCCESS
      setOfferModalOpen(false);
      setOfferAmount("");
      setOfferMessage("");
    } catch (err) {
      const code = err?.data?.code;
      const reqId = err?.data?.reqId || err?.data?.req_id;
      const reqSuffix = reqId ? ` (reqId ${reqId})` : "";
      if (notify) {
        if (code) {
          notify({
            title: "Offer failed",
            body: `Offer failed: ${code}${reqSuffix}`,
            kind: "error",
          });
        } else {
          notify({
            title: "Offer failed",
            body: err?.message || "Could not submit your offer, please try again.",
            kind: "error",
          });
        }
      }

      setOfferError(err?.message || "Could not submit your offer, please try again.");
    } finally {
      setOfferLoading(false);
    }
  };

  const buyerOffersErrorMessage =
    typeof buyerOffersError === "string" ? buyerOffersError : buyerOffersError?.message;
  const buyerOffersErrorCode =
    typeof buyerOffersError === "string" ? null : buyerOffersError?.code;
  const buyerOffersAuthError =
    buyerOffersAuthBlockedRef.current ||
    buyerOffersErrorCode === "AUTH_REQUIRED" ||
    buyerOffersErrorCode === "401" ||
    buyerOffersErrorCode === "403";

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 text-white">
      <VisitorNavBar showBackButton backTo="/discover" />

      <div className="relative pt-24 pb-12 px-4">
        <style>{`
          .mudo-select option {
            color: #fff !important;
            background: #0f1115 !important;
          }
        `}</style>
        <div className="bg-[#1a1b1e]/80 backdrop-blur-md border-b border-white/10 sticky top-0 z-30">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate("/discover")}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>
                <div>
                  <h1 className="text-2xl font-bold">Marketplace</h1>
                  <p className="text-sm text-white/60">
                    Browse ceramics, tools & materials from studios
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => setInboxOpen(true)}
                  className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
                  title="Messages"
                >
                  <Mail className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </button>

                <button
                  onClick={() => setProfileModalOpen(true)}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                  title="Profile"
                >
                  <User className="w-5 h-5" />
                </button>

                <button
                  onClick={() => {
                    try {
                      localStorage.setItem(
                        "mudo:switchToStudioIntentFrom",
                        window.location.pathname + window.location.search
                      );
                    } catch {}
                    localStorage.setItem("mudo:userRole", "studio");
                    navigate("/dashboard");
                  }}
                  className="px-4 py-2 rounded-lg bg-white/5 hover:bg白/10 border border-white/10 text-sm transition-colors"
                >
                  Switch to Studio
                </button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[200px] relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500/50 text-sm"
                />
              </div>

              {categories.length > 0 && (
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="mudo-select px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500/50 cursor-pointer"
                  style={{ minWidth: "140px" }}
                >
                  <option value="all">All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              )}

              {locations.length > 0 && (
                <select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="mudo-select px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500/50 cursor-pointer"
                  style={{ minWidth: "140px" }}
                >
                  <option value="all">All Locations</option>
                  {locations.map((loc) => (
                    <option key={loc} value={loc}>
                      {loc}
                    </option>
                  ))}
                </select>
              )}

              <div className="flex flex-col gap-1">
                <span className="text-[11px] text-white/60">Filter by studio</span>
                <select
                  value={selectedStudio}
                  onChange={(e) => setSelectedStudio(e.target.value)}
                  className="mudo-select px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500/50 cursor-pointer bg-neutral-900 text-white border border-white/20"
                  style={{ minWidth: "150px" }}
                >
                  <option value="all">All Studios</option>
                  {studios.map((studio) => (
                    <option key={studio.id} value={studio.id}>
                      {studio.name}
                    </option>
                  ))}
                </select>
              </div>

              <input
                type="number"
                placeholder="Min price ($)"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
                className="w-24 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-green-500/50 text-sm"
                title="Minimum price filter"
              />
              <span className="text-white/50">-</span>
              <input
                type="number"
                placeholder="Max price ($)"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
                className="w-24 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-green-500/50 text-sm"
                title="Maximum price filter"
              />

              {(searchTerm ||
                minPrice ||
                maxPrice ||
                selectedStudio !== "all" ||
                selectedCategory !== "all" ||
                selectedLocation !== "all") && (
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setMinPrice("");
                    setMaxPrice("");
                    setSelectedStudio("all");
                    setSelectedCategory("all");
                    setSelectedLocation("all");
                  }}
                  className="px-3 py-2 rounded-lg bg-red-600/20 hover:bg-red-600/30 border border-red-600/30 text-red-400 text-sm transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {buyerOffersErrorMessage && (
          <div className="max-w-7xl mx-auto px-4 mt-4">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm">
              <div className="text-red-200">
                {buyerOffersErrorMessage}
                {buyerOffersErrorCode ? ` (${buyerOffersErrorCode})` : ""}
              </div>
              {buyerOffersAuthError ? (
                <button
                  onClick={() => navigate("/auth")}
                  className="px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 border border-red-500/40 text-red-200 text-xs font-semibold transition-colors"
                >
                  Sign in
                </button>
              ) : (
                <button
                  onClick={() => {
                    setBuyerOffersError(null);
                    buyerOffersAuthBlockedRef.current = false;
                    buyerOffersAuthNotifiedRef.current = false;
                    loadBuyerOffers();
                  }}
                  className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 text-white/80 text-xs font-semibold transition-colors"
                >
                  Retry
                </button>
              )}
            </div>
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4 py-8">
          {loading && (
            <>
              <div className="mb-6">
                <div className="h-5 w-44 rounded bg-white/10 animate-pulse" />
              </div>
              <MarketplaceSkeletonGrid count={12} />
            </>
          )}

          {error && !loading && (
            <div className="text-center py-20">
              <div className="mb-4 text-red-500">
                <ShoppingBag className="w-16 h-16 mx-auto" />
              </div>
              <p className="text-lg text-white mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-3 rounded-lg bg-green-600 hover:bg-green-700 transition-colors font-semibold"
              >
                Retry
              </button>
            </div>
          )}

          {!loading && !error && filteredProducts.length === 0 && (
            <div className="text-center py-20">
              <ShoppingBag className="w-16 h-16 mx-auto mb-4 text-white/30" />
              <p className="text-lg text-white/70 mb-2">
                {searchTerm || minPrice || maxPrice || selectedStudio !== "all"
                  ? "No products match your filters"
                  : "No products available yet"}
              </p>
              <p className="text-sm text-white/50">
                {searchTerm || minPrice || maxPrice || selectedStudio !== "all"
                  ? "Try adjusting your filters"
                  : "Check back soon for new items!"}
              </p>
            </div>
          )}

          {!loading && !error && filteredProducts.length > 0 && (
            <>
              <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-white/70">
                  {filteredProducts.length}{" "}
                  {filteredProducts.length === 1 ? "product" : "products"}
                </p>
                <div className="text-xs text-white/50">
                  <span className="text-amber-400">*</span> 5% of proceeds support Mudo Network
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredProducts.map((product) => (
                  <MarketplaceCard
                    key={product.id}
                    product={product}
                    isFavorite={favorites.has(product.id)}
                    onToggleFavorite={toggleFavorite}
                    onClick={() => handleViewProduct(product)}
                    onMessage={handleMessage}
                    onOffer={handleOffer}
                  />
                ))}
              </div>

              {(hasMore || loadingMore) && (
                <div className="mt-10 flex flex-col items-center justify-center gap-4">
                  <button
                    type="button"
                    onClick={loadMore}
                    disabled={!hasMore || loadingMore}
                    className="px-6 py-3 rounded-lg bg-white/10 hover:bg-white/15 border border-white/15 text-white/80 text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingMore ? "Loading…" : hasMore ? "Load more" : "No more products"}
                  </button>
                  {loadingMore && <MarketplaceSkeletonGrid count={4} />}
                </div>
              )}

              <div ref={loadMoreSentinelRef} />
            </>
          )}
        </div>

        {imageModalOpen && (
          <ImageModal
            images={modalImages}
            currentIndex={modalImageIndex}
            title={modalProductTitle}
            onClose={() => setImageModalOpen(false)}
            onNext={nextImage}
            onPrev={prevImage}
          />
        )}

        <ListingDetailModal
          open={detailModalOpen}
          onClose={() => {
            setDetailModalOpen(false);
            setSelectedListingId(null);
            setListingDetail(null);
            setDetailError(null);
          }}
          listing={listingDetail}
          loading={detailLoading}
          error={detailError}
          isFavorite={listingDetail ? favorites.has(listingDetail.id) : false}
          onToggleFavorite={toggleFavorite}
          onMessage={handleMessage}
          onOffer={handleOffer}
          onStudioClick={(slug) => {
            setDetailModalOpen(false);
            navigate(`/discover/studio/${slug}`);
          }}
        />

        <ComposeMessageModal
          isOpen={composeModalOpen}
          onClose={() => {
            setComposeModalOpen(false);
            setMessageRecipient(null);
            setMessageProduct(null);
          }}
          recipientId={messageRecipient}
          productId={messageProduct?.id}
          productTitle={messageProduct?.title}
          onSuccess={() => {
            alert("Message sent successfully!");
            // unreadCount is now managed by UnreadCountContext with Realtime subscription
          }}
        />

        <InboxModal
          isOpen={inboxOpen}
          initialThreadId={initialThreadId}
          onClose={() => {
            setInboxOpen(false);
            // unreadCount is now managed by UnreadCountContext with Realtime subscription
          }}
        />

        <OfferModal
          open={offerModalOpen}
          onClose={handleCloseOfferModal}
          onSubmit={(amount) => handleSubmitOffer(amount)}
          defaultAmount={offerProduct ? offerProduct.price_cents / 100 : ""}
          loading={offerLoading}
          error={offerError}
        />

        <ProfileSetupModal
          isOpen={profileModalOpen}
          onComplete={() => setProfileModalOpen(false)}
          onClose={() => setProfileModalOpen(false)}
        />

        <ShopPayNowModal
          open={payModal.open}
          clientSecret={payModal.clientSecret}
          onClose={() => setPayModal({ open: false, clientSecret: null, offerId: null, returnTo: null })}
        />
      </div>
    </div>
  );
}

/**
 * MarketplaceCard - Enhanced card with carousel, description, and action buttons
 * Shows:
 * - Product image carousel (multiple images with prev/next)
 * - Title (2-line clamp)
 * - Studio name
 * - Description preview (1-2 lines clamped)
 * - Price
 * - Make Offer and Message buttons
 * - Favorite heart button
 * - Sold watermark if applicable
 */
function MarketplaceCard({ product, isFavorite, onToggleFavorite, onClick, onMessage, onOffer }) {
  const { user } = useAuth() || {};
  const { notify } = useNotifications() || {};
  const [imageLoadError, setImageLoadError] = useState(false);

  // Get primary image (single image for grid card, not carousel)
  const primaryImageUrl = useMemo(() => {
    const primaryImage = getPrimaryProductImage(product);
    if (!primaryImage) return null;
    
    // Images should already be public URLs or valid image paths
    // Return as-is without conversion
    return primaryImage;
  }, [product.main_image_url, product.image_gallery]);

  // Dev logging: detect image resolution issues
  useEffect(() => {
    if (import.meta.env.DEV && !primaryImageUrl) {
      const availableFields = {
        main_image_url: !!product.main_image_url,
        image_gallery: !!product.image_gallery,
        id: product.id,
        title: product.title,
      };
      console.warn(
        `[MarketplaceCard] Product ${product.id} (${product.title}) has no image. Available fields:`,
        availableFields
      );
    }
  }, [product.id, primaryImageUrl]);

  const hasStock = product.stock_qty === null || product.stock_qty > 0;
  const soldState = getSoldState(product);
  const showSoldWatermark = soldState.isSold;

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick?.();
    }
  };

  const handleMessage = (e) => {
    e.stopPropagation();
    if (import.meta.env.DEV) {
      console.log('[MarketplaceCard] Message button clicked', { productId: product.id, title: product.title });
    }
    
    if (!user?.id) {
      notify?.({
        title: "Sign in required",
        body: "Please sign in to message the seller.",
        kind: "warning",
      });
      return;
    }
    
    if (import.meta.env.DEV) {
      console.log('[MarketplaceCard] Opening message modal for product', { productId: product.id });
    }
    onMessage?.(product);
  };

  const handleOffer = (e) => {
    e.stopPropagation();
    if (import.meta.env.DEV) {
      console.log('[MarketplaceCard] Offer button clicked', { productId: product.id, title: product.title });
    }
    
    if (!user?.id) {
      notify?.({
        title: "Sign in required",
        body: "Please sign in to make an offer.",
        kind: "warning",
      });
      return;
    }
    
    if (import.meta.env.DEV) {
      console.log('[MarketplaceCard] Opening offer modal for product', { productId: product.id });
    }
    onOffer?.(product);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      className="w-full text-left bg-[#2a2b2e] border border-white/8 rounded-xl overflow-hidden hover:border-white/20 transition-all duration-300 group cursor-pointer flex flex-col"
    >
      {/* Image carousel */}
      <div className="relative bg-gradient-to-br from-green-500/10 to-emerald-500/10 overflow-hidden" style={{ aspectRatio: "4 / 3" }}>
        {primaryImageUrl && !imageLoadError ? (
          <img
            src={primaryImageUrl}
            alt={product.title || "Product"}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
            decoding="async"
            width={400}
            height={300}
            onError={() => setImageLoadError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="w-12 h-12 text-white/20" />
          </div>
        )}

        {/* Favorite button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite(product.id);
          }}
          className={`absolute top-3 right-3 z-20 p-2 rounded-full backdrop-blur-md transition-all ${
            isFavorite
              ? "bg-red-600 text-white"
              : "bg-black/30 text-white/70 hover:bg-black/50 hover:text-white"
          }`}
          title={isFavorite ? "Remove from favorites" : "Add to favorites"}
        >
          <Heart className={`w-4 h-4 ${isFavorite ? "fill-current" : ""}`} />
        </button>

        {/* Sold watermark (pointer-events-none prevents blocking clicks) */}
        {showSoldWatermark && (
          <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
            <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-[rgba(209,34,34,0.4)] to-[#b51717]/70 backdrop-blur-sm" />
            <span className="relative px-6 py-1.5 text-lg font-black tracking-[0.35em] uppercase text-white drop-shadow-[0_0_20px_rgba(0,0,0,0.4)]">
              SOLD
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col gap-3 flex-grow">
        {/* Title */}
        <h3 className="text-base font-bold line-clamp-2 group-hover:text-green-400 transition-colors">
          {product.title}
        </h3>

        {/* Studio name */}
        {product.studio && (
          <div className="text-xs text-green-500 flex items-center gap-1">
            <Star className="w-3 h-3" />
            {product.studio.name}
          </div>
        )}

        {/* Description preview */}
        {product.description && (
          <p className="text-xs text-white/60 line-clamp-2 leading-relaxed">
            {product.description}
          </p>
        )}

        {/* Price */}
        <div className="text-lg font-bold text-green-400 mt-auto">
          {showSoldWatermark
            ? "Sold"
            : formatPrice(product.price_cents, product.currency)}
        </div>

        {/* Action buttons (only show if not sold) */}
        {!showSoldWatermark && (
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={handleMessage}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold transition-colors"
              title="Message seller"
            >
              <MessageCircle className="w-3.5 h-3.5" />
              Message
            </button>
            <button
              type="button"
              onClick={handleOffer}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold transition-colors"
              title="Make an offer"
            >
              <DollarSign className="w-3.5 h-3.5" />
              Offer
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ProductCard({
  product,
  isFavorite,
  onToggleFavorite,
  onMessage,
  onOffer,
  onImageClick,
  onStudioClick,
  onBuyerPayNow,
  onBuyerConfirmReceived,
  onBuyerReportIssue,
}) {
  const toThumbUrl = (u) => {
    if (typeof u !== "string") return u;
    // Our uploader stores `...-display.webp` and `...-thumb.webp` side-by-side.
    return u.replace(/-display\.webp(\?.*)?$/i, "-thumb.webp$1");
  };

  const displayImages =
    Array.isArray(product.images_display) && product.images_display.length
      ? product.images_display
      : Array.isArray(product.image_gallery) && product.image_gallery.length
      ? product.image_gallery
      : Array.isArray(product.images) && product.images.length
      ? product.images
      : Array.isArray(product.image_urls) && product.image_urls.length
      ? product.image_urls
      : product.main_image_url
      ? [product.main_image_url]
      : product.image_url
      ? [product.image_url]
      : [];

  const thumbImages =
    Array.isArray(product.images_thumb) && product.images_thumb.length
      ? product.images_thumb
      : displayImages.map(toThumbUrl);

  const images = thumbImages;

  const hasMultipleImages = images.length > 1;

  const hasStock = product.stock_qty === null || product.stock_qty > 0;
  const stockLabel =
    product.stock_qty === null
      ? "In stock"
      : product.stock_qty === 0
      ? "Out of stock"
      : `${product.stock_qty} in stock`;
  const soldState = getSoldState(product);
  const showSoldWatermark = soldState.isSold;
  const soldLocked = soldState.isSold;

  const buyerOffers = product.buyerOffers || [];
  const hasBuyerOffers = buyerOffers.length > 0;
  const activeOffer =
    buyerOffers.find((o) => o.status === "disputed") ||
    buyerOffers.find((o) => o.status === "completed") ||
    buyerOffers.find((o) => o.status === "handed_over") ||
    buyerOffers.find((o) => o.status === "accepted") ||
    buyerOffers.find((o) => o.status === "pending") ||
    buyerOffers[0] ||
    null;
  const activeOfferStatus = activeOffer?.status || "pending";
  const activeOrderStatus = activeOffer?.order_status || null;
  const isPaidHeld = activeOrderStatus === "paid_held";
  const isAccepted = activeOfferStatus === "accepted";
  if (import.meta.env?.DEV && activeOffer && isAccepted) {
    console.debug("[Discover] CTA logic", {
      offerId: activeOffer?.id,
      offerStatus: activeOffer?.status,
      orderStatus: activeOffer?.order_status,
      isPaidHeld,
      isAccepted,
    });
  }
  const progressSteps = [
    "Offer sent",
    "Offer accepted",
    "Payment held",
    "Seller fulfils",
    "Buyer confirms",
    "Payout released",
  ];
  const currentStepIndex = (() => {
    if (!activeOffer) return -1;
    if (activeOfferStatus === "disputed") return isPaidHeld ? 2 : 1;
    if (activeOfferStatus === "completed") return 5;
    if (activeOfferStatus === "handed_over") return isPaidHeld ? 3 : 2;
    if (activeOfferStatus === "accepted") return isPaidHeld ? 2 : 1;
    if (activeOfferStatus === "pending") return 0;
    return 0;
  })();
  const nextLabel = (() => {
    if (!activeOffer) return "";
    if (activeOfferStatus === "disputed") return "Issue reported";
    if (activeOfferStatus === "completed") return "Complete";
    if (activeOfferStatus === "handed_over" && isPaidHeld) {
      return "Next: Confirm received";
    }
    if (activeOfferStatus === "accepted" && !isPaidHeld) {
      return "Next: Pay now";
    }
    if (isPaidHeld && activeOfferStatus !== "handed_over") {
      return "Next: Seller fulfils / ships";
    }
    if (activeOfferStatus === "pending") return "Next: Seller accepts";
    return "Next: Pay now";
  })();
  const acceptedOffer =
    buyerOffers.find((o) => o.status === "accepted") || null;
  const eligibleConfirmationOffer =
    buyerOffers.find(
      (o) => o.status === "handed_over" && o.order_status === "paid_held"
    ) || null;

  return (
    <div
      id={`listing-${product.id}`}
      className="bg-[#2a2b2e] border border-white/8 rounded-xl overflow-hidden hover:border-white/20 transition-all duration-300 group"
    >
      <div
        className="relative bg-gradient-to-br from-green-500/10 to-emerald-500/10 overflow-hidden cursor-pointer"
        onClick={() => onImageClick(product)}
      >
        {images.length > 0 ? (
          <ImageCarousel
            images={images}
            alt={product.title || "Product"}
            aspect="4/3"
            className="rounded-none"
            showDots={false}
            showCounter={false}
            imgClassName="group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div
            className="w-full flex items-center justify-center"
            style={{ aspectRatio: "4 / 3" }}
          >
            <Package className="w-12 h-12 text-white/20" />
          </div>
        )}

        {hasMultipleImages && (
          <div className="absolute bottom-3 right-3 px-2 py-1 rounded-md bg-black/60 backdrop-blur-sm text-white text-xs font-semibold flex items-center gap-1">
            <Package className="w-3 h-3" />
            {images.length}
          </div>
        )}

        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite(product.id);
          }}
          className={`absolute top-3 right-3 z-20 p-2 rounded-full backdrop-blur-md transition-all ${
            isFavorite ? "bg-red-600 text-white" : "bg-black/30 text-white/70 hover:bg-black/50 hover:text-white"
          }`}
          title={isFavorite ? "Remove from favourites" : "Add to favourites"}
        >
          <Heart className={`w-4 h-4 ${isFavorite ? "fill-current" : ""}`} />
        </button>

        {showSoldWatermark && (
          <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
            <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-[rgba(209,34,34,0.4)] to-[#b51717]/70 backdrop-blur-sm" />
            <div className="relative text-center space-y-1">
              <span className="relative px-6 py-1.5 text-base sm:text-lg font-black tracking-[0.35em] uppercase text-white drop-shadow-[0_0_20px_rgba(0,0,0,0.4)]">
                SOLD
              </span>
              {soldState.isInGrace && (
                <span className="text-xs uppercase text-white/80 tracking-[0.25em]">
                  Auto-unpublishes in {soldState.graceHoursLeft || 0}h
                </span>
              )}
            </div>
          </div>
        )}

        {!hasStock && (
          <div className="absolute top-3 left-3 z-20 px-2 py-1 rounded-md bg-red-600 text-white text-xs font-semibold">
            Out of Stock
          </div>
        )}
        {hasStock && product.stock_qty !== null && product.stock_qty <= 3 && (
          <div className="absolute top-3 left-3 z-20 px-2 py-1 rounded-md bg-orange-600 text-white text-xs font-semibold">
            Only {product.stock_qty} left
          </div>
        )}
      </div>

      <div className="p-4">
        <h3 className="text-base font-bold mb-2 line-clamp-2 group-hover:text-green-400 transition-colors">
          {product.title}
        </h3>

        {product.studio && (
          <button
            onClick={onStudioClick}
            className="text-xs text-green-500 hover:text-green-400 mb-2 hover:underline flex items-center gap-1"
          >
            <Star className="w-3 h-3" />
            {product.studio.name}
          </button>
        )}

        {product.description && (
          <p className="text-xs text-white/60 mb-3 line-clamp-2">{product.description}</p>
        )}

        <div className="text-xl font-bold text-green-400 mb-3">
          {showSoldWatermark
            ? "Sold"
            : formatPrice(product.price_cents, product.currency)}
        </div>

        {hasBuyerOffers && (
          <div className="mb-3 rounded-lg border border-white/10 bg-white/5 px-2 py-2">
            <div className="text-[11px] text-white/60 mb-2">Marketplace progress</div>
            <div className="flex gap-1 mb-2">
              {progressSteps.map((label, idx) => (
                <div
                  key={label}
                  className={`h-1 flex-1 rounded-full ${
                    idx <= currentStepIndex ? "bg-emerald-400" : "bg-white/15"
                  }`}
                />
              ))}
            </div>
            <div className="text-[10px] text-white/50 leading-relaxed">
              {progressSteps.join(" | ")}
            </div>
            <div className="mt-1 text-[11px] text-white/70">{nextLabel}</div>
          </div>
        )}

        {!soldLocked && acceptedOffer && (
          <div className="mb-3 flex items-center justify-between gap-2">
            <span className="px-2 py-1 text-xs rounded-full bg-emerald-500/20 text-emerald-200 border border-emerald-400/40">
              Offer accepted
            </span>
            <button
              onClick={() => !isPaidHeld && onBuyerPayNow(acceptedOffer)}
              disabled={isPaidHeld}
              className={`px-3 py-1.5 rounded-lg text-black text-xs font-semibold shadow-lg transition-colors ${
                isPaidHeld
                  ? "bg-gray-500 cursor-not-allowed opacity-60"
                  : "bg-emerald-500 hover:bg-emerald-400 shadow-emerald-500/30"
              }`}
            >
              {isPaidHeld ? "Payment held — awaiting seller" : "Pay now"}
            </button>
          </div>
        )}

        {eligibleConfirmationOffer && !soldLocked && (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <button
              onClick={() => onBuyerConfirmReceived(eligibleConfirmationOffer)}
              className="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-semibold shadow-lg shadow-emerald-500/30 transition-colors"
            >
              Confirm received
            </button>
            <button
              onClick={() => onBuyerReportIssue(eligibleConfirmationOffer)}
              className="px-3 py-1.5 rounded-lg bg-red-600/20 hover:bg-red-600/30 border border-red-600/30 text-red-400 text-xs font-semibold transition-colors"
            >
              Report issue
            </button>
          </div>
        )}

        {!soldLocked && (
          <div className="flex gap-2 mb-2">
            <button
              onClick={() => onMessage(product)}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold transition-colors"
              title="Message seller"
            >
              <MessageCircle className="w-3.5 h-3.5" />
              Message
            </button>
            <button
              onClick={() => onOffer(product)}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold transition-colors"
              title="Make an offer"
            >
              <DollarSign className="w-3.5 h-3.5" />
              Offer
            </button>
          </div>
        )}

        <p className="text-xs text-white/40 text-center">{stockLabel}</p>
      </div>
    </div>
  );
}

function ImageModal({ images, currentIndex, title, onClose, onNext, onPrev }) {
  const hasMultiple = images.length > 1;

  useEffect(() => {
      const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && hasMultiple) onPrev();
      if (e.key === "ArrowRight" && hasMultiple) onNext();
      };

      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
  }, [hasMultiple, onClose, onNext, onPrev]);

  return (
      <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center">
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10"
        title="Close (Esc)"
      >
        <X className="w-6 h-6" />
      </button>

      <div className="absolute top-4 left-4 text-white z-10">
        <h2 className="text-xl font-bold">{title}</h2>
        {hasMultiple && (
          <p className="text-sm text-white/70 mt-1">
            {currentIndex + 1} / {images.length}
          </p>
        )}
      </div>

      {hasMultiple && (
        <button
          onClick={onPrev}
          className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10"
          title="Previous (<-)"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
      )}

      <div className="max-w-6xl max-h-[90vh] w-full h-full flex items-center justify-center p-4">
        <img
          src={images[currentIndex]}
          alt={`${title} - Image ${currentIndex + 1}`}
          className="max-w-full max-h-full object-contain rounded-lg"
        />
      </div>

      {hasMultiple && (
        <button
          onClick={onNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10"
          title="Next (->)"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      )}

      {hasMultiple && images.length <= 10 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={() => {
                if (idx === currentIndex) return;
                const diff = idx - currentIndex;
                if (diff > 0) {
                  for (let i = 0; i < diff; i += 1) onNext();
                } else {
                  for (let i = 0; i < Math.abs(diff); i += 1) onPrev();
                }
              }}
              className={`w-16 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                idx === currentIndex ? "border-green-500 scale-110" : "border-white/30 hover:border-white/60"
              }`}
            >
              <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
      </div>
  );
}
